import { Configuration, ConfigurationParameters } from 'generated/api'
import { KeycloakAuthMiddleware } from 'services/FionaAPI/templates/middleware/KeycloakAuthMiddleware'
import { AuthAlerts, ExternalAuthState } from 'services/Authentication'
import {
	FionaAPIError,
	HttpBadRequest,
	HttpConflict,
	HttpUnauthorized,
	UnknownApiResponseError
} from 'services/FionaAPI/templates/errors'
import { isServer, Optional } from 'lib'
import { NotInBrowserError } from 'lib/errors'
import { addDDError } from 'services/AnalyticsLite/Datadog/events'

export const createConfiguration = (config: ConfigurationParameters = {}) => {
	return new Configuration({
		...config,
		middleware: [KeycloakAuthMiddleware]
	})
}

type Operation<Args extends any[], Return> = (...operationParameters: Args) => Promise<Return>

type ErrorHandler = (response: Response) => Promise<unknown>

export const getErrorMessage = async (response: Response): Promise<Optional<string>> => {
	const body = await response.json()
	return body?.error?.message
}

const handleBaseErrors = async (response: Response) => {
	let error
	switch (response.status) {
		case 400:
			error = new HttpBadRequest(await response.json())
			throw error
		case 401:
			await ExternalAuthState.alert(AuthAlerts.API_401)
			await ExternalAuthState.getToken()
			break
		case 403:
			error = new HttpUnauthorized(response)
			throw error
		case 409:
			error = new HttpConflict(response)
			throw error
		default:
			error = new UnknownApiResponseError(response)
			addDDError(error)
			throw error
	}
}

export const createFionaClient = <Args extends any[], Return>(
	operation: Operation<Args, Return>,
	errorHandler?: ErrorHandler
): Operation<Args, Return> => {
	return async (...parameters) => {
		let attempts = 0
		if (isServer()) throw new NotInBrowserError()
		while (attempts <= 2) {
			attempts++
			try {
				return await operation(...parameters)
			} catch (err) {
				if (err instanceof Error) {
					addDDError(err)
				} else {
					const response = err as Response
					errorHandler && (await errorHandler(response))
					await handleBaseErrors(response)
				}
			}
		}
		throw new FionaAPIError(operation.toString())
	}
}
